/**
 * tm/pages/MobileLoan.tsx — 휴대폰 「대출 링크 보내기」 (2026-10-14 지도사 「고객이 대출 알아봐 달라고 하면 내 폰에서 이런 링크를 바로 보낼 수 있게 · 사용하기 쉽게」)
 *  ① 임지도사 링크(/loan/imjidosa · 팀장님 없음) — 「보내기」 한 번 = 휴대폰 공유(문자 · 카톡 고르기) · 안 되면 글째 복사
 *  ② 팀장님 소개 고객이면 그 팀장님 링크 — 이름 · 번호 뒷자리로 찾기 → 누르면 같은 공유(업체가 팀장님과 이어져 팀장님 포털에도 진행이 보임)
 *  ③ 받는 분 고르기(2026-10-14 지도사 「내가 팀장님에게 보내는 경우 많음」) — 기본 「팀장님께」: 팀장님을 찾아 그 팀장님 링크 + 「팀장님, …」 글 · 「대표님께」: 예전 화면(임지도사 링크 · 팀장님 링크 + 「대표님, …」 글) · 고른 쪽은 이 폰이 기억
 *  보험사 이름은 대출 화면에 쓰지 않는다(정책자금 부당영업 오해 · 2026-10-14 토스 디자이너 역할 모의 검토) — 번호 뒷자리로 구분
 *  ④ 없는 팀장님은 여기서 바로 등록(이름 · 휴대폰 · 소속 · 2026-10-22 지도사) → 목록 맨 위에 나오고 「보내기」
 *  문구(2026-10-22 지도사 「3분이면 적으실 수 있어요 같은 영업 냄새 나는 멘트 안 쓴다」) — 무엇을 보내는지 · 무엇을 하면 되는지만 담담하게
 */
import { useMemo, useState } from 'react';
import type { FP } from '../types';
import * as api from '../api';
import { G, clamp } from '../portal-ui/tds';
import { Button, Card, CardHead, Chips, TOSS, Tint, input } from './mobileKit';

const BASE = 'https://roum-partners.com/loan/';
const DIRECT = 'imjidosa';
const MSG = '대표님, 정책자금 대출 검토를 위한 접수 링크입니다.\n회사 정보와 가지고 계신 서류를 올려 주시면 검토 후 연락드리겠습니다.';
/** 받는 분 호칭 그대로(팀장 · 지점장 …) */
// 팀장님께 보내는 링크 = ?w=fp(열면 팀장님이 적기가 기본 · 대표님께는 화면의 「대표님께 보내기」 — 2026-10-20 지도사)
const FP_W = '?w=fp';
/** 팀장님께 — 주소 두 개(2026-10-22 지도사): 팀장님이 적는 주소(?w=fp) · 대표님께 그대로 전달할 주소(같은 팀장님 링크 · ?w 없음)
 *  예전엔 「화면의 대표님께 보내기를 눌러 주세요」였는데, 문자를 그대로 전달하면 대표님 화면이 「팀장님이 대신 적고 있어요」로 열렸다 */
//  2026-10-23 지도사 「호칭이 대표님인 팀장님은 헷갈린다 · 줄 띄우기가 안 맞다」 — 받는 분 호칭을 안내 줄에 쓰지 않는다(「직접 입력하실 때 · 고객사 대표님께 전달하실 때」)
// 열쇠(&k=)는 문자에 싣지 않는다 — 두 주소가 한 문자라 통째로 대표님께 넘어가면 팀장님의 다른 대출이 보였다(2026-10-28 검토) · 인증번호 없이 보기는 팀장님 포털을 연 기기에서만
const fpMsg = (name = '', title = '팀장', url = '') => `${name ? name + ' ' : ''}${title}님, 정책자금 대출 검토 접수 링크입니다.\n\n직접 입력하실 때\n${url}${FP_W}\n\n고객사 대표님께 전달하실 때\n${url}`;
const MSG_FP_DIRECT = '팀장님, 정책자금 대출 검토 접수 링크입니다.\n고객사 대표님께 전달 부탁드립니다.';   // 임지도사 링크(팀장님 없음)는 「대표님께 보내기」가 없다
const TO_KEY = 'roum.loanLinkTo';
type To = 'fp' | 'ceo';

/** 휴대폰 공유창(문자 · 카톡) — 없거나 막히면 글째 복사 · url 이 비면 주소가 글 안에 들어 있는 것(팀장님께 · 주소 두 개) */
async function share(url: string, toast: (m: string) => void, msg: string = MSG) {
  const nav = navigator as Navigator & { share?: (d: { title?: string; text?: string; url?: string }) => Promise<void> };
  if (typeof nav.share === 'function') {
    // 글 하나로만 보낸다 — title 을 주면 카톡이 「정책자금 대출 검토」를 첫 줄에 붙이고, url 을 따로 주면 앱마다 띄우기가 달랐다(2026-10-23 지도사 「첫 줄 · 둘째 줄 띄어쓰기」)
    try { await nav.share({ text: url ? `${msg}\n\n${url}` : msg }); return; } catch (e) { if (e instanceof Error && e.name === 'AbortError') return; }
  }
  await copy(url ? `${msg}\n\n${url}` : msg, toast, '복사했어요 · 문자나 카톡에 붙여 넣으세요');
}
async function copy(text: string, toast: (m: string) => void, done = '링크를 복사했어요') {
  try { await navigator.clipboard.writeText(text); toast(done); } catch { toast('복사하지 못했어요 · 링크를 길게 눌러 복사해 주세요'); }
}

const digits = (v: string) => String(v || '').replace(/\D/g, '');
/** 010-1234-5678 꼴 */
const fmtTel = (v: string) => { const d = digits(v).slice(0, 11); return d.length === 11 ? `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}` : d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : d; };

export default function MobileLoan({ fps, toast, refresh }: { fps: FP[]; toast: (m: string) => void; refresh?: () => Promise<void> }) {
  const [q, setQ] = useState('');
  /* 새 팀장님 등록(이름 · 휴대폰 · 소속) — 2026-10-22 */
  const [adding, setAdding] = useState(false);
  const [nm, setNm] = useState('');
  const [tel, setTel] = useState('');
  const [org, setOrg] = useState('');
  const [saving, setSaving] = useState(false);
  const [fresh, setFresh] = useState<FP[]>([]);   // 방금 등록한 팀장님(목록이 새로 올 때까지 맨 위에)
  const orgs = useMemo(() => Array.from(new Set(fps.map(f => f.company).filter(Boolean))).sort(), [fps]);
  const dup = digits(tel).length >= 10 ? [...fresh, ...fps].find(f => digits(f.phone || '') === digits(tel)) : undefined;
  const lack = !nm.trim() ? '이름을 적어 주세요' : !/^01\d{8,9}$/.test(digits(tel)) ? '휴대폰 번호를 적어 주세요' : dup ? '이미 등록된 번호예요' : '';
  const openAdd = () => { setNm(/\d/.test(q) ? '' : q.trim()); setTel(/\d/.test(q) ? q.trim() : ''); setOrg(''); setAdding(true); };
  async function saveFp() {
    if (lack || saving) { if (lack) toast(lack); return; }
    setSaving(true);
    try {
      const r = await api.createFp({ name: nm.trim(), phone: fmtTel(tel), company: org.trim(), wm_id: null });
      if (!r.success || !r.data) throw new Error(r.error || '등록하지 못했어요');
      const f = r.data;
      setFresh(l => [f, ...l.filter(x => x.id !== f.id)]);
      setAdding(false); setQ('');
      toast(`${f.name} 팀장님을 등록했어요 · 「보내기」를 눌러 주세요`);
      if (refresh) void refresh();
    } catch (e) { toast(e instanceof Error ? e.message : '등록하지 못했어요'); }
    finally { setSaving(false); }
  }
  const [to, setTo] = useState<To>(() => { try { return window.localStorage.getItem(TO_KEY) === 'ceo' ? 'ceo' : 'fp'; } catch { return 'fp'; } });
  const pick = (v: To) => { setTo(v); try { window.localStorage.setItem(TO_KEY, v); } catch { /* 저장 못 해도 화면은 그대로 */ } };
  const fpMode = to === 'fp';
  const list = useMemo(() => {
    const k = q.replace(/\s/g, '');
    const L = [...fresh, ...fps.filter(f => !fresh.some(x => x.id === f.id))].filter(f => f.is_active !== false && f.loan_token);
    const hit = k ? L.filter(f => `${f.name}${String(f.phone || '').replace(/\D/g, '')}`.replace(/\s/g, '').includes(k)) : L;
    const isNew = (f: FP) => fresh.some(x => x.id === f.id);
    return hit.slice().sort((a, b) => (isNew(b) ? 1 : 0) - (isNew(a) ? 1 : 0) || a.name.localeCompare(b.name, 'ko')).slice(0, k ? 20 : 8);
  }, [fps, q, fresh]);
  const url = BASE + DIRECT;

  return (
    <div style={{ padding: '8px 16px 32px' }}>
      <div style={{ margin: '0 0 12px' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: TOSS.sub, margin: '4px 4px 8px' }}>누구에게 보내세요?</div>
        <Chips cols={2} items={[{ k: 'fp', t: '팀장님께' }, { k: 'ceo', t: '대표님께' }]} value={[to]} onChange={v => v[0] && pick(v[0] as To)} />
      </div>

      {!fpMode && <Card pad="18px 20px 20px">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Tint icon="won" />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 18, fontWeight: 700, color: TOSS.text }}>대표님께 바로 보내기</span>
            <span style={{ display: 'block', fontSize: 15, color: TOSS.sub, marginTop: 2 }}>「임지도사가 보내 드렸어요」로 열려요</span>
          </span>
        </div>
        <div style={{ margin: '14px 0 12px', padding: '12px 14px', borderRadius: 12, background: TOSS.bg, fontSize: 15, color: TOSS.sub, lineHeight: 1.5, wordBreak: 'keep-all', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>
          {MSG}<br /><span style={{ color: TOSS.blueDk }}>{url.replace('https://', '')}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8 }}>
          <Button onClick={() => void share(url, toast)} ariaLabel="대출 링크 보내기">보내기</Button>
          <Button kind="gray" onClick={() => void copy(`${MSG}\n${url}`, toast, '문자 내용을 복사했어요 · 카톡에 붙여 넣어 주세요')} ariaLabel="문자 내용 복사">문자 내용 복사</Button>
        </div>
      </Card>}

      <Card pad="4px 0 8px">
        <CardHead title={fpMode ? '팀장님께 보내기' : '팀장님이 소개한 고객이면'} />
        <div style={{ padding: '0 20px 8px', fontSize: 15, color: TOSS.sub, wordBreak: 'keep-all' }}>{fpMode ? '그 팀장님 링크로 가요 · 접수되면 팀장님 화면에도 진행이 보여요' : '팀장님 링크로 보내면 팀장님 화면에도 진행이 보여요'}</div>
        {fpMode && <div style={{ margin: '4px 20px 12px', padding: '12px 14px', borderRadius: 12, background: TOSS.bg, fontSize: 15, color: TOSS.sub, lineHeight: 1.5, wordBreak: 'keep-all', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{fpMsg('', '팀장', 'roum-partners.com/loan/…')}</div>}
        <div style={{ padding: '0 20px 8px' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="팀장님 이름 · 번호 뒷자리" aria-label="팀장님 찾기" style={{ ...input, background: TOSS.bg, border: 'none' }} />
        </div>
        {list.length === 0 && <div style={{ padding: '16px 20px', fontSize: 15, color: G.g600 }}>{q ? '찾는 팀장님이 없어요' : '대출 링크가 있는 팀장님이 없어요'}</div>}
        {fpMode && list.length === 0 && <div style={{ padding: '0 20px 12px' }}><Button kind="gray" h={52} onClick={() => void share(url, toast, MSG_FP_DIRECT)} ariaLabel="임지도사 링크로 팀장님께 보내기">임지도사 링크로 보내기</Button></div>}
        {adding ? (
          <div style={{ margin: '4px 20px 12px', padding: '14px 14px 16px', borderRadius: 14, background: TOSS.bg, display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: TOSS.text }}>새 팀장님 등록</div>
            <input value={nm} onChange={e => setNm(e.target.value)} placeholder="이름" aria-label="팀장님 이름" style={input} />
            <input value={tel} onChange={e => setTel(e.target.value)} onBlur={() => setTel(v => fmtTel(v))} placeholder="휴대폰 010-0000-0000" inputMode="tel" aria-label="팀장님 휴대폰" style={input} />
            {dup && <div style={{ fontSize: 15, color: '#D22030' }}>이미 등록된 번호예요 · {dup.name} <button type="button" className="tds-btn" onClick={() => { setAdding(false); setQ(dup.name); }} style={{ border: 'none', background: 'none', color: TOSS.blueDk, fontSize: 15, fontWeight: 700, padding: '0 4px', cursor: 'pointer', fontFamily: 'inherit' }}>찾기</button></div>}
            <input value={org} onChange={e => setOrg(e.target.value)} list="m-loan-orgs" placeholder="소속 (보험사 · 지점)" aria-label="팀장님 소속" style={input} />
            <datalist id="m-loan-orgs">{orgs.map(o => <option key={o} value={o} />)}</datalist>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 8, marginTop: 4 }}>
              <Button kind="gray" h={52} onClick={() => setAdding(false)} ariaLabel="등록 그만두기">그만두기</Button>
              <Button h={52} muted={!!lack || saving} onClick={() => void saveFp()} ariaLabel="팀장님 등록">{saving ? '등록 중' : '등록'}</Button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '0 20px 8px' }}>
            <button type="button" className="tds-btn" onClick={openAdd}
              style={{ width: '100%', minHeight: 48, borderRadius: 12, border: `1px dashed ${G.g400}`, background: '#fff', color: TOSS.blueDk, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              목록에 없으면 새 팀장님 등록
            </button>
          </div>
        )}
        {list.map(f => (
          <button key={f.id} type="button" className="tds-row" onClick={() => void (fpMode ? share('', toast, fpMsg(f.name, f.title || '팀장', BASE + f.loan_token)) : share(BASE + f.loan_token, toast, MSG))} aria-label={fpMode ? `${f.name} 팀장님께 보내기` : `${f.name} 팀장님 링크로 대표님께 보내기`}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 64, padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{f.name} {f.title || '팀장'}님</span>
              {String(f.phone || '').replace(/\D/g, '').length >= 4 && <span style={{ display: 'block', fontSize: 15, color: TOSS.sub, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>번호 뒷자리 {String(f.phone).replace(/\D/g, '').slice(-4)}</span>}
            </span>
            <span style={{ flexShrink: 0, fontSize: 16, fontWeight: 700, color: TOSS.blue }}>보내기</span>
          </button>
        ))}
      </Card>
    </div>
  );
}
